import { calculateAccuracy } from "@helpers/common";
import { getCurrentDate } from "@helpers/date";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import type { BaseItemSchema } from "@memorang/types/src/session/new-session";
import type { MixedItemTag } from "@memorang/types/src/tag";
import Box from "@memorang/ui/src/components/Box";
import {
	ReportQRCode,
	ReportWatermark,
} from "@memorang/ui/src/components/QrCodeAndWatermark";
import ScrollContainer from "@memorang/ui/src/components/ScrollContainer";
import { Image } from "expo-image";
import { useLocalSearchParams } from "expo-router";
import React from "react";
import type { ScrollView } from "react-native";
import { DataTable, Text } from "react-native-paper";
import { useReactToPrint } from "react-to-print";
import {
	calculateTagPoints,
	calculateTotalItemsPoints,
	findLowestLevelTag,
	formatDate,
} from "./helpers";

type StudentReportData = {
	appName: string;
	id: string;
	email: string;
	numericId: number;
	assessment?: string;
	assessmentId: number;
	exam: string;
	accuracy: string;
	tags: MixedItemTag[];
	items?: BaseItemSchema[];
};

const TableHeader = ({ children }: { children: React.ReactNode }) => (
	<DataTable.Header
		style={{
			borderBottomWidth: 1,
		}}
	>
		{children}
	</DataTable.Header>
);

const TableTitle = ({
	flex,
	children,
	numeric = false,
}: {
	flex: number;
	children: React.ReactNode;
	numeric?: boolean;
}) => (
	<DataTable.Title style={{ flex, paddingVertical: 4 }} numeric={numeric}>
		{children}
	</DataTable.Title>
);

const TableRow = ({
	children,
	isFooter = false,
}: {
	children: React.ReactNode;
	isFooter?: boolean;
}) => {
	const theme = useAppTheme();
	return (
		<DataTable.Row
			style={{
				minHeight: 34,
				...(isFooter && {
					backgroundColor: theme.colors.surfaceVariant,
				}),
			}}
		>
			{children}
		</DataTable.Row>
	);
};

const TableCell = ({
	flex,
	children,
	paddingLeft,
	numeric = false,
}: {
	flex: number;
	children: React.ReactNode;
	paddingLeft?: number;
	numeric?: boolean;
}) => (
	<DataTable.Cell
		style={{ flex, ...(paddingLeft && { paddingLeft }) }}
		numeric={numeric}
	>
		{children}
	</DataTable.Cell>
);

type FooterCellConfig = {
	flex: number;
	content: React.ReactNode;
	numeric?: boolean;
	id: string;
};

const TableFooter = ({ cells }: { cells: FooterCellConfig[] }) => {
	const theme = useAppTheme();
	return (
		<DataTable.Row
			style={{
				backgroundColor: theme.colors.surfaceVariant,
				minHeight: 34,
			}}
		>
			{cells.map((cell) => (
				<DataTable.Cell
					key={cell.id}
					style={{ flex: cell.flex }}
					numeric={cell.numeric}
				>
					<Text variant="labelMedium">{cell.content}</Text>
				</DataTable.Cell>
			))}
		</DataTable.Row>
	);
};

const TopicItem = ({
	tag,
	depth = 0,
}: { tag: MixedItemTag; depth?: number }) => {
	const accuracy = calculateAccuracy({
		numCorrect: tag.numCorrect ?? 0,
		numTotal: tag.numItems ?? 0,
	});

	return (
		<React.Fragment key={tag.label}>
			<TableRow>
				<TableCell flex={4}>
					<Text variant="bodySmall" style={{ paddingLeft: depth * 16 }}>
						{tag.code ? `${tag.code} - ${tag.label}` : tag.label || tag.title}
					</Text>
				</TableCell>
				<TableCell flex={1} numeric>
					<Text variant="bodySmall">{`${accuracy}%`}</Text>
				</TableCell>
				<TableCell flex={1} numeric>
					<Text variant="bodySmall">{`${tag.numPointsEarned || tag.numCorrect} of ${tag.numTotalPoints || tag.numItems}`}</Text>
				</TableCell>
			</TableRow>

			{tag.children?.map((subTag) => (
				<TopicItem key={subTag.label} tag={subTag} depth={depth + 1} />
			))}
		</React.Fragment>
	);
};

const TopicsTable = ({
	tags,
	accuracy,
}: { tags: MixedItemTag[]; accuracy: string }) => {
	const { pointsEarned: totalPointsEarned, totalPoints } =
		calculateTagPoints(tags);

	return (
		<Box gap={8}>
			<Text variant="titleLarge" style={{ fontWeight: "bold" }}>
				Topics
			</Text>
			<DataTable
				style={{
					borderWidth: 1,
				}}
			>
				<TableHeader>
					<TableTitle flex={4}>Subject/Category</TableTitle>
					<TableTitle flex={1} numeric>
						Accuracy
					</TableTitle>
					<TableTitle flex={1} numeric>
						Points
					</TableTitle>
				</TableHeader>

				{tags.map((tag) => (
					<TopicItem key={tag.label} tag={tag} />
				))}

				<TableFooter
					cells={[
						{ flex: 4, content: `Total: ${tags.length}`, id: "topics-total" },
						{
							flex: 1,
							content: accuracy,
							numeric: true,
							id: "topics-accuracy",
						},
						{
							flex: 1,
							content: `${totalPointsEarned} of ${totalPoints}`,
							numeric: true,
							id: "topics-points",
						},
					]}
				/>
			</DataTable>
		</Box>
	);
};

const InfoItem = ({ label, value }: { label: string; value: string }) => (
	<Box flexDirection="row" alignItems="flex-start" gap={24}>
		<Text variant="titleSmall" style={{ minWidth: 120 }}>
			{label}:
		</Text>
		<Text style={{ flex: 1 }}>{value}</Text>
	</Box>
);

const ItemsTable = ({ items }: { items?: BaseItemSchema[] }) => {
	if (!items?.length) {
		return null;
	}

	const { pointsEarned, totalPoints } = calculateTotalItemsPoints(items);

	return (
		<Box gap={8}>
			<Text variant="titleLarge" style={{ fontWeight: "bold" }}>
				Items
			</Text>
			<DataTable
				style={{
					borderWidth: 1,
				}}
			>
				<TableHeader>
					<TableTitle flex={1}>Item ID</TableTitle>
					<TableTitle flex={2}>Topic</TableTitle>
					<TableTitle flex={1}>Status</TableTitle>
					<TableTitle flex={1}>Marked</TableTitle>
					<TableTitle flex={1} numeric>
						Points
					</TableTitle>
				</TableHeader>

				{items.map((item) => {
					const lowestLevelTag = findLowestLevelTag(item.tags ?? []);

					return (
						<TableRow key={item.id}>
							<TableCell flex={1}>
								<Text variant="bodySmall">{`ITEM-${item.id}`}</Text>
							</TableCell>
							<TableCell flex={2}>
								<Text variant="bodySmall" style={{ paddingRight: 16 }}>
									{lowestLevelTag.code
										? `${lowestLevelTag.code} - ${lowestLevelTag.label}`
										: lowestLevelTag.label || lowestLevelTag.title}
								</Text>
							</TableCell>
							<TableCell flex={1}>
								<Text variant="bodySmall">
									{item.answer?.selectedChoiceIds?.length
										? item.answer?.correct
											? "✓ Correct"
											: "✗ Incorrect"
										: "◌ Skipped"}
								</Text>
							</TableCell>
							<TableCell flex={1}>
								<Text variant="bodySmall">
									{item.answer?.marked ? "⚑" : ""}
								</Text>
							</TableCell>
							<TableCell flex={1} numeric>
								<Text variant="bodySmall">
									{`${item.answer?.correct ? 1 : 0} of 1`}
								</Text>
							</TableCell>
						</TableRow>
					);
				})}

				<TableFooter
					cells={[
						{
							flex: 1,
							content: `Total: ${items.length}`,
							id: "items-total",
						},
						{ flex: 2, content: items.length, id: "items-count" },
						{
							flex: 1,
							content: (
								<>
									✓ {items.filter((i) => i.answer?.correct).length}, ✗{" "}
									{
										items.filter(
											(i) =>
												i.answer?.selectedChoiceIds?.length &&
												!i.answer?.correct,
										).length
									}
									, ◌{" "}
									{
										items.filter((i) => !i.answer?.selectedChoiceIds?.length)
											.length
									}
								</>
							),
							id: "items-status",
						},
						{
							flex: 1,
							content: `⚑ ${items.filter((i) => i.answer?.marked).length}`,
							id: "items-marked",
						},
						{
							flex: 1,
							content: `${pointsEarned} of ${totalPoints}`,
							numeric: true,
							id: "items-points",
						},
					]}
				/>
			</DataTable>
		</Box>
	);
};

const PrintableReport = ({
	data,
	onCancel,
}: { data: StudentReportData; onCancel: () => void }) => {
	const scrollRef = React.useRef<ScrollView>(null);
	const [imageLoaded, setImageLoaded] = React.useState(false);

	const downloadFileName = `${data.appName}-${data.assessmentId}-${formatDate()}.pdf`;
	const reactToPrintFn = useReactToPrint({
		contentRef: scrollRef as unknown as React.RefObject<HTMLDivElement>,
		onAfterPrint: () => {
			onCancel();
		},
		documentTitle: downloadFileName,
	});
	const appLogo = useAppStore((store) => store.app.logo);
	const { print } = useLocalSearchParams<{ print?: string }>();
	const isPrintMode = print === "true";

	const baseUrl = useAppStore((store) => store.app.links.baseUrl);

	React.useEffect(() => {
		if (isPrintMode && imageLoaded) {
			reactToPrintFn();
		}
	}, [isPrintMode, imageLoaded, reactToPrintFn]);

	const infoItems = [
		{ label: "ID", value: data.id },
		{ label: "Email", value: data.email },
		...(data.assessment
			? [{ label: "Assessment", value: data.assessment }]
			: []),
		{ label: "Assessment ID", value: data.assessmentId },
		{ label: "Exam", value: data.exam },
		{ label: "Accuracy", value: data.accuracy },
	];

	const qrCodeUrl = `${baseUrl}/session/${data.numericId}/report`;

	return (
		<ScrollContainer
			ref={scrollRef}
			contentContainerStyle={{ padding: 20, position: "relative" }}
		>
			<ReportWatermark appName={data.appName} />

			<Box gap={24} style={{ position: "relative", zIndex: 2 }}>
				<Box
					flexDirection="row"
					justifyContent="space-between"
					alignItems="center"
				>
					<ReportQRCode url={qrCodeUrl} />
					<Image
						source={{ uri: appLogo.default.light.uri }}
						style={{ width: 100, height: 56 }}
						contentFit="contain"
						onLoad={() => setImageLoaded(true)}
					/>
				</Box>

				<Box gap={24}>
					<Text variant="headlineMedium" style={{ fontWeight: "bold" }}>
						{data.appName} Score Report
					</Text>

					<Box gap={8}>
						{infoItems.map((item) => (
							<InfoItem
								key={item.label}
								label={item.label}
								value={`${item.value}`}
							/>
						))}
					</Box>
				</Box>

				<TopicsTable tags={data.tags} accuracy={data.accuracy} />

				<Box alignSelf="flex-end" paddingTop={34}>
					<Text variant="bodySmall">
						Legend: ✓ Correct, ✗ Incorrect, ◌ Skipped, ⚑ Marked
					</Text>
				</Box>

				<ItemsTable items={data.items} />

				<Text variant="bodyMedium">Report Generated: {getCurrentDate()}</Text>
			</Box>
		</ScrollContainer>
	);
};

export default PrintableReport;
